<script setup lang="ts">
defineOptions({ inheritAttrs: false })
const props = withDefaults(
  defineProps<{
    open: boolean
    anchor?: HTMLElement
    id?: string
    label: string
    role?: 'menu' | 'dialog' | 'tooltip'
    width?: number | 'trigger'
    minWidth?: number
    placement?: 'start' | 'end'
    autofocus?: boolean
  }>(),
  {
    anchor: undefined,
    id: undefined,
    role: 'dialog',
    width: 360,
    minWidth: 0,
    placement: 'end',
    autofocus: true,
  },
)
const emit = defineEmits<{ close: [] }>()
const panel = ref<HTMLElement>()
const route = useRoute()
const { style, ownerId, sensitive, position } = useUiFloating(
  toRef(props, 'open'),
  () => props.anchor,
  panel,
  () => props,
)
const focusables = () =>
  [
    ...(panel.value?.querySelectorAll<HTMLElement>(
      'a[href],button:not(:disabled),input:not(:disabled),[tabindex="0"]',
    ) ?? []),
  ].filter((e) => e.getClientRects().length)
function close(restore = false) {
  emit('close')
  if (restore) props.anchor?.focus()
}
function outside(event: PointerEvent) {
  if (
    props.open &&
    !panel.value?.contains(event.target as Node) &&
    !props.anchor?.contains(event.target as Node)
  )
    close()
}
function focusout() {
  setTimeout(() => {
    if (
      props.open &&
      panel.value &&
      !panel.value.contains(document.activeElement) &&
      !props.anchor?.contains(document.activeElement)
    )
      close()
  }, 0)
}
function keydown(event: KeyboardEvent) {
  if (!props.open) return
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    close(true)
  } else if (props.role === 'menu' && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
    event.preventDefault()
    const items = focusables(),
      current = items.indexOf(document.activeElement as HTMLElement)
    const index =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : (current + (event.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length
    items[index]?.focus()
  } else if (event.key === 'Tab' && props.role === 'menu') close(true)
}
watch(
  () => props.open,
  async (open) => {
    if (!open) return
    await nextTick()
    position()
    if (props.autofocus) (focusables()[0] ?? panel.value)?.focus()
  },
  { immediate: true, flush: 'post' },
)
watch(
  () => route.fullPath,
  () => {
    if (props.open) close()
  },
)
onMounted(() => {
  document.addEventListener('pointerdown', outside)
  document.addEventListener('keydown', keydown, true)
})
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', outside)
  document.removeEventListener('keydown', keydown, true)
})
</script>
<template>
  <Teleport to="body">
    <section
      v-if="open"
      v-bind="$attrs"
      :id="id"
      ref="panel"
      class="ui-popover"
      :class="{ 'analytics-sensitive': sensitive }"
      :style="style"
      :data-ui-overlay-owner="ownerId"
      :role="role"
      :aria-label="label"
      tabindex="-1"
      @focusout="focusout"
    >
      <slot />
    </section>
  </Teleport>
</template>
